import { FormEvent, useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { api, qs } from '../lib/api'
import { tagPath } from '../lib/format'
import { useSettings } from '../lib/settings'
import { Comic, Page, TagInfo } from '../lib/types'
import { useLoad } from '../lib/useLoad'
import { ComicGrid, Feed, Pagination } from '../ui/comic'
import { Icon } from '../ui/Icon'
import { Layout } from '../ui/Layout'
import { RandomButton } from '../ui/RandomButton'
import { EmptyState, Loading, PostSkeleton } from '../ui/states'

type Mode = 'home' | 'tag' | 'search'

function SearchAgain({ initial }: { initial: string }) {
  const navigate = useNavigate()
  const [q, setQ] = useState(initial)
  useEffect(() => {
    setQ(initial)
  }, [initial])
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`)
  }
  return (
    <form role="search" className="searchbox lg mb-2xl" onSubmit={submit}>
      <input type="search" aria-label="Search comics" value={q} onChange={(e) => setQ(e.target.value)} />
      <button type="submit" aria-label="Search">
        <Icon name="search" size={20} />
      </button>
    </form>
  )
}

/** Home, tag and search pages: a feed of full comics, 10 per page (tag pages can be a grid of thumbnails instead) */
export function FeedPage({ mode }: { mode: Mode }) {
  const { tag = '' } = useParams()
  const [params] = useSearchParams()
  const page = Number(params.get('page')) || 1
  const q = mode === 'search' ? params.get('q') ?? '' : ''
  const { settings } = useSettings()

  const load = useLoad(
    (signal) => api.get<Page<Comic> & { tag?: TagInfo }>(`/api/comics${qs({ tag: mode === 'tag' ? tag : '', q, page })}`, signal),
    [mode, tag, q, page]
  )

  // The tag as most comics spell it, once loaded
  const info = load.state === 'ready' ? load.data.tag : undefined
  const tagName = info?.name ?? tag

  useEffect(() => {
    document.title =
      mode === 'tag' ? `${tagName} · What a comical life` : mode === 'search' ? `Search: ${q} · What a comical life` : 'What a comical life'
  }, [mode, tagName, q])
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [page])

  const base = mode === 'tag' ? tagPath(tag) : mode === 'search' ? '/search' : '/'
  const href = (p: number) => `${base}${qs({ q, page: p })}`

  let heading = null
  if (mode === 'tag') {
    const paragraphs = (info?.description ?? '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)
    heading = (
      <header className="vstack gap-sm mb-3xl">
        <div className="eyebrow">Tag</div>
        <h1 className="h1 ink">{tagName}</h1>
        {paragraphs.length > 0 && (
          <div className="vstack gap-md mt-sm">
            {paragraphs.map((p, i) => (
              <p key={i} className="body-text">
                {p}
              </p>
            ))}
          </div>
        )}
      </header>
    )
  } else if (mode === 'search') {
    heading = (
      <>
        <h1 className="vh">Search results</h1>
        <SearchAgain initial={q} />
      </>
    )
  } else {
    heading = <h1 className="vh">The latest comics</h1>
  }

  let body
  if (load.state === 'loading') {
    body = (
      <Loading label="Loading comics">
        <PostSkeleton />
        <PostSkeleton />
      </Loading>
    )
  } else if (load.state === 'error') {
    body = (
      <EmptyState
        error
        headingLevel={2}
        title="Unable to load comics"
        actions={
          <div className="btns">
            <button type="button" className="btn solid" onClick={load.retry}>
              Try again
            </button>
            {mode !== 'home' && (
              <Link className="btn" to="/">
                Go to home
              </Link>
            )}
          </div>
        }
      >
        Something went wrong on our end. Check your connection and try again.
      </EmptyState>
    )
  } else if (load.data.total === 0) {
    if (mode === 'tag') {
      body = (
        <EmptyState
          headingLevel={2}
          title="No comics with this tag yet"
          actions={
            <div className="btns">
              <Link className="btn solid" to="/">
                Browse all comics
              </Link>
              <RandomButton label="Random comic" />
            </div>
          }
        >
          Comics tagged {tagName} will appear here. Try another tag or browse everything.
        </EmptyState>
      )
    } else if (mode === 'search') {
      body = (
        <EmptyState
          icon="search"
          headingLevel={2}
          title={`No results for "${q}"`}
          actions={
            settings.sidebarTags.length > 0 && (
              <div className="chips">
                {settings.sidebarTags.map((t) => (
                  <Link key={t} className="chip" to={tagPath(t)}>
                    {t}
                  </Link>
                ))}
              </div>
            )
          }
        >
          Check the spelling, try a shorter word, or browse a tag.
        </EmptyState>
      )
    } else {
      body = (
        <EmptyState
          headingLevel={2}
          title="No comics yet"
          actions={
            <div className="btns">
              <a className="btn solid" href="#nl-email">
                Join the newsletter
              </a>
            </div>
          }
        >
          New comics will show up here. Check back soon, or join the newsletter to get them in your inbox.
        </EmptyState>
      )
    }
  } else {
    body = (
      <>
        {mode === 'search' && (
          <p className="hint strong mb-xl" aria-live="polite">
            {load.data.total === 1 ? '1 comic found' : `${load.data.total} comics found`}
          </p>
        )}
        {info?.layout === 'grid' ? <ComicGrid comics={load.data.items} /> : <Feed comics={load.data.items} />}
        <Pagination page={load.data.page} totalPages={load.data.totalPages} href={href} />
      </>
    )
  }

  return (
    <Layout>
      <div className="column">
        {heading}
        {body}
      </div>
    </Layout>
  )
}
